import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { FormDataService } from 'src/app/shared/attachments/service/form-data.service';
import {
  MainTableFilterModel,
  PrintReportFilterModel,
} from 'src/app/shared/models/main-table-filter.model';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from 'src/environments/environment';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { ProductAction } from '../enums/product-action.enum';
import { IProductStock } from '../models/product-stock.model';

import { IUseProduct } from '../models/use-product.model';
import {
  IApiResponseGroupProducts,
  IProductListItem,
  IProductResponse,
  IProductRequest,
  ITransferProductRequest,
} from '../models/product.model';

const COLUMN_TO_KEY_MAP: any = {
  currentQuantity: 'Quantity',
  productName: 'Name',
  critical: 'IsCritical',
  chemical: 'IsChemical',
  components: 'Components.Name',
  quantityType: 'QuantityType.Name',
  department: 'Department.Name',
};

@Injectable({
  providedIn: 'root',
})
export class ProductApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/Product/StorageProduct`;
  }
  private get allDepsUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/Product/StorageProduct`;
  }

  constructor(private http: HttpClient) {}

  getProducts(
    filter?: MainTableFilterModel,
    menuId?: string,
    allDeps?: boolean
  ): Observable<IApiResponseGroupProducts> {
    let url = '';
    let params = createFilterParams(filter);
    if (allDeps) {
      url = `${this.allDepsUrl}`;
      params = params.set('ExcludeArchived', 'true');
    } else {
      url = `${this.url}`;
    }

    if (menuId) {
      params = params.append('MenuId', menuId);
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP
    );

    return this.http.get<IApiResponseGroupProducts>(url, { params });
  }

  getAllProducts(
    excludeAttachedToMaintenanceJobId?: string,
    excludeAttachedToComponentId?: string
  ): Observable<IProductListItem[]> {
    const url = `${this.url}/List`;

    let params = new HttpParams();
    if (excludeAttachedToMaintenanceJobId) {
      params = params.append(
        'ExcludeAttachedToMaintenanceJobId',
        excludeAttachedToMaintenanceJobId
      );
    }
    if (excludeAttachedToComponentId) {
      params = params.append('ExcludeAttachedToComponentId', excludeAttachedToComponentId);
    }

    return this.http.get<IProductListItem[]>(url, { params });
  }

  getProductById(productId: string, productDepartmentId?: string): Observable<IProductResponse> {
    let url = '';
    if (productDepartmentId) {
      url = `${
        environment.apiUrl
      }/api/${getTenantId()}/${productDepartmentId}/Product/StorageProduct/${productId}`;
    } else {
      url = `${this.url}/${productId}`;
    }
    return this.http.get<IProductResponse>(url);
  }

  createProduct(product: IProductRequest): Observable<IProductResponse> {
    const formData = FormDataService.mapToFormData(product);
    return this.http.post<IProductResponse>(this.url, formData);
  }

  updateProduct(product: IProductRequest): Observable<IProductResponse> {
    const formData = FormDataService.mapToFormData(product);
    const url = `${this.url}/${product.storageProductDetails.commonDetails.id}`;
    return this.http.put<IProductResponse>(url, formData);
  }

  deleteProduct(id: string) {
    const url = `${this.url}/${id}`;
    return this.http.delete<IProductResponse>(url);
  }

  useProduct(product: IUseProduct, productDepartmentId?: string): Observable<IProductResponse> {
    let url = '';
    if (productDepartmentId) {
      url = `${
        environment.apiUrl
      }/api/${getTenantId()}/${productDepartmentId}/Product/StorageProduct/${ProductAction.Use}`;
    } else {
      url = `${this.url}/${ProductAction.Use}`;
    }
    const formData = FormDataService.mapToFormData(product);
    return this.http.put<IProductResponse>(url, formData);
  }

  addtoStock(product: IUseProduct, productDepartmentId?: string): Observable<IProductResponse> {
    let url = '';
    if (productDepartmentId) {
      url = `${
        environment.apiUrl
      }/api/${getTenantId()}/${productDepartmentId}/Product/StorageProduct/${ProductAction.Add}`;
    } else {
      url = `${this.url}/${ProductAction.Add}`;
    }
    const formData = FormDataService.mapToFormData(product);
    return this.http.put<IProductResponse>(url, formData);
  }

  adjustStock(product: IUseProduct): Observable<IProductResponse> {
    const formData = FormDataService.mapToFormData(product);
    const url = `${this.url}/${ProductAction.Adjust}`;
    return this.http.put<IProductResponse>(url, formData);
  }

  getProductStock(
    productId: string,
    currentFilter?: MainTableFilterModel
  ): Observable<IProductStock[]> {
    const url = `${this.url}/${productId}/Stock`;
    const params = createFilterParams(currentFilter);

    return this.http.get<IProductStock[]>(url, { params });
  }

  printReport(filter?: PrintReportFilterModel, menuId?: string): Observable<Blob> {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    let url = '';
    if (allDeps) {
      url = `${this.allDepsUrl}/Report`;
    } else {
      url = `${this.url}/Report`;
    }

    let params = createFilterParams(filter);
    if (menuId) {
      params = params.set('MenuId', menuId);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP
    );
    return this.http.get(url, { params, responseType: 'blob' });
  }

  getMatchingProducts(
    sourceStorageProductId: string,
    targetDepartmentId: string
  ): Observable<IProductResponse[]> {
    const url = `${this.url}/${sourceStorageProductId}/WithMatchingName`;
    const params = new HttpParams().set('TargetDepartmentId', targetDepartmentId);
    return this.http.get<IProductResponse[]>(url, { params });
  }

  transferProduct(transferRequest: ITransferProductRequest): Observable<void> {
    const url = `${this.url}/${transferRequest.sourceStorageProductId}/Transfer`;
    return this.http.post<void>(url, transferRequest);
  }
}
